import { useState, useEffect } from 'react';
import styles from './NFSeDetailsModal.module.css';

interface NFSeDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    lancamentoData: {
        id: number;
        numeroNFSE?: string;
        numeroRPS?: string;
        nfseId?: number;
        dataEmissaoNFSE?: string;
    };
    isAdmin: boolean;
    onCancelNFSe: (lancamentoId: number, motivo: string) => Promise<void>;
    onUpdateNFSe?: (lancamentoId: number, dadosNFSE: { numeroNFSE?: string; numeroRPS?: string; nfseId?: number; dataEmissao?: string }) => Promise<void>;
}

export default function NFSeDetailsModal({
    isOpen,
    onClose,
    lancamentoData,
    isAdmin,
    onCancelNFSe,
    onUpdateNFSe,
}: NFSeDetailsModalProps) {
    const [motivo, setMotivo] = useState('');
    const [isCanceling, setIsCanceling] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editForm, setEditForm] = useState({
        numeroNFSE: '',
        numeroRPS: '',
        nfseId: '',
        dataEmissao: ''
    });

    useEffect(() => {
        if (isOpen && lancamentoData) {
            setEditForm({
                numeroNFSE: lancamentoData.numeroNFSE || '',
                numeroRPS: lancamentoData.numeroRPS || '',
                nfseId: lancamentoData.nfseId ? String(lancamentoData.nfseId) : '',
                dataEmissao: lancamentoData.dataEmissaoNFSE ? new Date(lancamentoData.dataEmissaoNFSE).toISOString().slice(0, 16) : ''
            });
            setIsEditing(false);
            setShowCancelConfirm(false);
            setMotivo('');
        }
    }, [isOpen, lancamentoData]);

    if (!isOpen) return null;

    const handleCancelClick = async () => {
        if (!motivo.trim()) {
            alert('Por favor, informe um motivo para o cancelamento.');
            return;
        }

        setIsCanceling(true);
        try {
            await onCancelNFSe(lancamentoData.id, motivo);
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setIsCanceling(false);
        }
    };

    const handleSaveClick = async () => {
        if (!onUpdateNFSe) return;
        setIsSaving(true);
        try {
            await onUpdateNFSe(lancamentoData.id, {
                numeroNFSE: editForm.numeroNFSE || undefined,
                numeroRPS: editForm.numeroRPS || undefined,
                nfseId: editForm.nfseId ? Number(editForm.nfseId) : undefined,
                dataEmissao: editForm.dataEmissao ? new Date(editForm.dataEmissao).toISOString() : undefined,
            });
        } catch (error) {
            console.error(error);
        } finally {
            setIsSaving(false);
            setIsEditing(false);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Não informada';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }).format(date);
        } catch (e) {
            return dateString;
        }
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2>Detalhes da NFSe no Bling</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        &times;
                    </button>
                </div>

                <div className={styles.content}>
                    <div className={styles.infoRow}>
                        <strong>Número da NFSe:</strong>
                        {isEditing ? (
                            <input type="text" value={editForm.numeroNFSE} onChange={e => setEditForm({ ...editForm, numeroNFSE: e.target.value })} className={styles.editInput} />
                        ) : (
                            <span>{lancamentoData.numeroNFSE || 'Aguardando sincronização'}</span>
                        )}
                    </div>

                    <div className={styles.infoRow}>
                        <strong>Número do RPS:</strong>
                        {isEditing ? (
                            <input type="text" value={editForm.numeroRPS} onChange={e => setEditForm({ ...editForm, numeroRPS: e.target.value })} className={styles.editInput} />
                        ) : (
                            <span>{lancamentoData.numeroRPS || 'Não informado'}</span>
                        )}
                    </div>

                    <div className={styles.infoRow}>
                        <strong>ID da Nota (Bling):</strong>
                        {isEditing ? (
                            <input type="number" value={editForm.nfseId} onChange={e => setEditForm({ ...editForm, nfseId: e.target.value })} className={styles.editInput} />
                        ) : (
                            <span>{lancamentoData.nfseId || 'Não informado'}</span>
                        )}
                    </div>

                    <div className={styles.infoRow}>
                        <strong>Data de Emissão:</strong>
                        {isEditing ? (
                            <input type="datetime-local" value={editForm.dataEmissao} onChange={e => setEditForm({ ...editForm, dataEmissao: e.target.value })} className={styles.editInput} />
                        ) : (
                            <span>{formatDate(lancamentoData.dataEmissaoNFSE)}</span>
                        )}
                    </div>

                    {isAdmin && (
                        <div className={styles.adminActions}>
                            {isEditing ? (
                                <div className={styles.saveActions}>
                                    <button className={styles.cancelActionBtn} onClick={() => setIsEditing(false)} disabled={isSaving}>Cancelar Edição</button>
                                    <button className={styles.confirmActionBtn} onClick={handleSaveClick} disabled={isSaving}>{isSaving ? 'Salvando...' : 'Salvar Informações'}</button>
                                </div>
                            ) : (
                                <button className={styles.editButton} onClick={() => setIsEditing(true)}>Editar Informações da NFSe</button>
                            )}
                        </div>
                    )}

                    {isAdmin && lancamentoData.nfseId && !isEditing && (
                        <div className={styles.cancelSection}>
                            {!showCancelConfirm ? (
                                <button
                                    className={styles.dangerButton}
                                    onClick={() => setShowCancelConfirm(true)}
                                >
                                    Cancelar esta NFSe no Bling
                                </button>
                            ) : (
                                <div className={styles.confirmCancelBox}>
                                    <h4>Confirmar Cancelamento</h4>
                                    <p>Atenção: Esta ação cancelará a nota no Bling (e na prefeitura, se autorizado via API).</p>

                                    <div className={styles.inputGroup}>
                                        <label>Motivo do cancelamento (obrigatório):</label>
                                        <textarea
                                            value={motivo}
                                            onChange={(e) => setMotivo(e.target.value)}
                                            placeholder="Ex: Erro nos dados do tomador, duplicidade, etc."
                                            rows={3}
                                        />
                                    </div>

                                    <div className={styles.actionsBox}>
                                        <button
                                            className={styles.cancelActionBtn}
                                            onClick={() => setShowCancelConfirm(false)}
                                            disabled={isCanceling}
                                        >
                                            Voltar
                                        </button>
                                        <button
                                            className={styles.confirmActionBtn}
                                            onClick={handleCancelClick}
                                            disabled={isCanceling || !motivo.trim()}
                                        >
                                            {isCanceling ? 'Cancelando...' : 'Confirmar Cancelamento'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
